import React from "react";
import { clamp, range } from "lodash";

/**
 * Return an element to show pagination text
 *
 * @param {Number} start the first row of the current page
 * @param {Number} to the last row of the current page
 * @param {Number} total the number of all rows
 * @returns {React.Element<'span'>}
 */
export const renderTotal = (start, to, total) => {
  /* case for 0 elements */
  const showing = to && start ? to - start + 1 : 0;
  return (
    <span style={{ marginLeft: "15px" }}>
      Showing {showing} of {total} entries
    </span>
  );
};

/**
 * Return a string for an empty page
 *
 * @param {Boolean} loading
 * @returns {String}
 */
export const renderNoData = loading =>
  loading ? "Loading..." : "There is no data to display";

/**
 * Custom renderPaginationPanel function for react-bootstrap-table
 * http://allenfang.github.io/react-bootstrap-table/custom.html#pagination
 *
 * @param {Object} props
 * @param {Object} props.components
 * @returns {React.Element<'div'>}
 */
export const renderPaginationPanel = ({ components = {} } = {}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      width: "100%",
      padding: "0 15px",
    }}>
    <div style={{ display: "flex", alignItems: "center" }}>
      {components.sizePerPageDropdown}
      {components.totalText}
    </div>
    <div>{components.pageList}</div>
  </div>
);

/**
 * @param {Number} page the current page
 * @param {Number} totalPages the number of pages
 * @returns {Array}
 */
export const calcPagesList = (page, totalPages) => {
  const delta = 2;
  const maxDisplayedPages = delta * 2 + 1;
  const first = 1;
  const last = totalPages;
  const center = clamp(page, first + delta, last - delta);
  const left = last <= maxDisplayedPages ? first : center - delta;
  const right = last <= maxDisplayedPages ? last : center + delta;

  return range(left, right + 1);
};

/**
 * Get indexes of the first/last row of the current page.
 * The algorithm is almost the same as that of [react-bootstrap-table](https://github.com/AllenFang/react-bootstrap-table/blob/v4.3.1/src/pagination/PaginationList.js#L105-L108).
 *
 * @param {Number} page
 * @param {Number} size
 * @param {Number} total
 */
export const calcStartAndTo = (page, size, total) => {
  let start = (page - 1) * size;
  start = total === 0 ? 0 : start + 1;
  let to = Math.min(page * size, total);

  return { start, to };
};
